/**
 * referralDocumentExport.js
 * =========================
 * Which referral documents staff can download as separate PDFs for EMIS/EIS
 * entry (generated by the rmsExportReferralDocument callable):
 *
 *   - one respondent's answers to one referral form or parent/carer triage
 *     questionnaire. The Holistic Functional Profile is never exported.
 *   - one pathway's triage decision, once that pathway's triage is complete.
 *
 * The server enforces both rules; these helpers only decide which download
 * actions to offer.
 */

import {
  isCombinedConcern,
  pathwaysForConcern,
  pathwayReviews,
  pathwayTriage,
  PATHWAY_LABELS,
} from './triagePathways';
import { EMIS_FORM_LABELS } from './emisFileNaming';

/**
 * Form keys whose responses can be downloaded. Mirrors _EXPORTABLE_FORMS in
 * python-functions/rms_functions/rms_referral_document_export.py — keep in
 * sync. No HFP keys, of any generation.
 */
export const EXPORTABLE_FORM_KEYS = [
  'requestForReferral',
  'reasonForReferral',
  'educationHistory',
  'snapIV26',
  'theirEarlyLife',
  'theirFamilyAndHomeLife',
  'theirLifestyle',
  'theirHistoryWithTheLaw',
  'theirFamilyHistory',
  'theirMedicalHistory',
  'theirMentalHealthHistory',
];

/**
 * @param {string} formKey - base (unsuffixed) form key
 * @returns {boolean}
 */
export function isExportableFormKey(formKey) {
  return EXPORTABLE_FORM_KEYS.includes(formKey);
}

/**
 * The forms bundled into one respondent's combined questionnaire PDF, in
 * journey order. SNAP-IV downloads on its own. Mirrors
 * _RESPONDENT_FORM_ORDER in rms_referral_document_export.py.
 */
export const RESPONDENT_FORM_KEYS = EXPORTABLE_FORM_KEYS.filter((key) => key !== 'snapIV26');

const RESPONDENTS = [
  { role: 'school_user', label: 'School' },
  { role: 'guardian', label: 'Parent or carer' },
];

/**
 * A respondent's instance of a form: the role-suffixed instance, or a legacy
 * unsuffixed one recorded as that respondent's. Same rule as the server.
 */
function respondentInstance(formInstances, formKey, role) {
  const suffixedId = `${formKey}_${role}`;
  if (formInstances[suffixedId]) return { instanceId: suffixedId, instance: formInstances[suffixedId] };
  const legacy = formInstances[formKey];
  if (legacy && legacy?.context?.currentUser?.role === role) return { instanceId: formKey, instance: legacy };
  return null;
}

const hasAnswers = (instance) => Object.keys(instance?.answers || {}).length > 0;

/**
 * The Documents-tab downloads for a referral, in display order:
 *   - one combined PDF per respondent of every questionnaire they answered
 *     (school; parent or carer, including the triage questionnaires);
 *   - each respondent's SNAP-IV as its own PDF, wherever SNAP-IV applies
 *     (ADHD or combined referrals, or a SNAP-IV already answered);
 *   - each completed pathway's triage decision.
 * The HFP is never included. Rows with nothing to download yet stay listed
 * with `available: false` so staff can see what is still outstanding.
 *
 * @param {Record<string, object>|null} formInstances - instanceId -> instance
 * @param {object|null} patientRecord
 * @returns {{ key: string, title: string, detail: string, available: boolean, request: object }[]}
 */
export function documentDownloads(formInstances, patientRecord, { perForm = false, uploads = [] } = {}) {
  const instances = formInstances || {};
  const rows = [];

  if (perForm) {
    // TDI-388 (Tom King, 26 October 2026): the ADHD service files each form
    // as its own PDF, so separate-documents surfaces list one named row per
    // answered form instead of the combined respondent bundles. Only forms
    // that exist are listed — the handover runs once the forms are in.
    RESPONDENTS.forEach(({ role, label }) => {
      RESPONDENT_FORM_KEYS.forEach((formKey) => {
        const found = respondentInstance(instances, formKey, role);
        if (!found) return;
        const available = hasAnswers(found.instance);
        rows.push({
          key: `form:${found.instanceId}`,
          title: `${EMIS_FORM_LABELS[formKey] || formKey} completed by ${label.toLowerCase()}`,
          detail: available
            ? (found.instance.status === 'submitted' ? 'Submitted' : 'In progress')
            : 'No answers yet',
          available,
          request: { document: 'form', instanceId: found.instanceId },
        });
      });
    });
  } else {
    RESPONDENTS.forEach(({ role, label }) => {
      const answered = RESPONDENT_FORM_KEYS
        .map((formKey) => respondentInstance(instances, formKey, role))
        .filter((found) => found && hasAnswers(found.instance));
      const count = answered.length;
      rows.push({
        key: `respondent:${role}`,
        title: `${label} questionnaire answers`,
        detail: count
          ? `${count} questionnaire${count === 1 ? '' : 's'} in one PDF`
          : 'No answers yet',
        available: count > 0,
        request: { document: 'respondentForms', role },
      });
    });
  }

  const concern = String(patientRecord?.intakeConcern || '').trim().toLowerCase();
  const snapFound = RESPONDENTS.map(({ role }) => respondentInstance(instances, 'snapIV26', role));
  const snapApplies = concern === 'adhd' || concern === 'both' || snapFound.some(Boolean);
  if (snapApplies) {
    RESPONDENTS.forEach(({ role, label }, i) => {
      const found = snapFound[i];
      const available = Boolean(found && hasAnswers(found.instance));
      let detail = 'No answers yet';
      if (available) detail = found.instance.status === 'submitted' ? 'Submitted' : 'In progress';
      rows.push({
        key: `form:${found ? found.instanceId : `snapIV26_${role}`}`,
        title: `SNAP-IV completed by ${label.toLowerCase()}`,
        detail,
        available,
        request: { document: 'form', instanceId: found ? found.instanceId : `snapIV26_${role}` },
      });
    });
  }

  // TDI-612 (TDI-602 FR10): on separate-documents pathways (ADHD) each
  // document a school or family uploaded is its own row, after SNAP-IV and
  // before the outcome letter, in the server's order (school first, then
  // parent or carer, oldest first). Never on the combined bundles, where the
  // autism pack carries them instead (TDI-613).
  if (perForm) rows.push(...uploadDownloadRows(uploads));

  downloadableTriageDecisions(patientRecord).forEach(({ pathway, label }) => {
    rows.push({
      key: `decision:${pathway || 'referral'}`,
      title: label,
      detail: 'Triage complete',
      available: true,
      request: { document: 'triageDecision', pathway },
    });
  });

  return rows;
}

/** The tick and row key of the evidence pack (TDI-389). */
export const EVIDENCE_PACK_KEY = 'evidencePack';

/**
 * The Admin Handover downloads for a pathway that files one evidence pack
 * (TDI-385/389; autism today), in display order:
 *   - the evidence pack: the parent or carer's and the school's
 *     questionnaires and each SNAP-IV answered, merged behind a contents page
 *     by rmsExportReferralDocument ({ document: 'evidencePack', pathway });
 *   - the triage decision for this pathway, once its triage is complete. It
 *     is not part of the pack, so it stays its own document.
 * The pack is offered once any part of it has answers; before that the row
 * stays listed as not available, since the server would refuse an empty pack.
 *
 * @param {Record<string, object>|null} formInstances - instanceId -> instance
 * @param {object|null} patientRecord
 * @param {string|null} pathway - the pathway being handed over ('adhd' | 'asd')
 * @returns {{ key: string, title: string, detail: string, available: boolean, request: object }[]}
 */
export function evidencePackDownloads(formInstances, patientRecord, pathway) {
  const instances = formInstances || {};
  const packKeys = [...RESPONDENT_FORM_KEYS, 'snapIV26'];
  const anyAnswered = RESPONDENTS.some(({ role }) => packKeys.some((formKey) => {
    const found = respondentInstance(instances, formKey, role);
    return Boolean(found && hasAnswers(found.instance));
  }));
  const rows = [{
    key: EVIDENCE_PACK_KEY,
    title: 'Evidence pack',
    detail: anyAnswered
      ? 'Parent or carer and school questionnaires and SNAP-IV, with a contents page'
      : 'No answers yet',
    available: anyAnswered,
    request: { document: 'evidencePack', pathway: pathway || null },
  }];

  // Only this pathway's decision: on a combined referral the other pathway
  // is handed over, and filed, from its own tab.
  const combined = isCombinedConcern(patientRecord?.intakeConcern);
  downloadableTriageDecisions(patientRecord)
    .filter((decision) => !combined || decision.pathway === pathway)
    .forEach(({ pathway: decisionPathway, label }) => {
      rows.push({
        key: `decision:${decisionPathway || 'referral'}`,
        title: label,
        detail: 'Triage complete',
        available: true,
        request: { document: 'triageDecision', pathway: decisionPathway },
      });
    });

  return rows;
}

/**
 * The triage decisions that can be downloaded now: one entry per pathway
 * whose triage is complete with at least one recorded review. A pathway still
 * in triage (including a pending second review) is never offered, so a first
 * review cannot leak to a potential second reviewer.
 *
 * Combined referrals list each pathway separately; single-concern referrals
 * list their one pathway; unclassified referrals list one entry with a null
 * pathway (the legacy single decision record).
 *
 * @param {object|null} patientRecord
 * @returns {{ pathway: string|null, label: string }[]}
 */
export function downloadableTriageDecisions(patientRecord) {
  if (!patientRecord) return [];
  const concern = patientRecord.intakeConcern;
  const pathways = pathwaysForConcern(concern);
  const candidates = pathways.length ? pathways : [null];
  const combined = isCombinedConcern(concern);

  return candidates
    .filter((pathway) => {
      const triage = pathwayTriage(patientRecord, pathway) || {};
      return triage.status === 'complete' && pathwayReviews(patientRecord, pathway).length > 0;
    })
    .map((pathway) => ({
      // The server ignores the pathway on single-concern and unclassified
      // referrals; only a combined referral needs it to pick a record.
      pathway: combined ? pathway : null,
      label: pathway ? `${PATHWAY_LABELS[pathway]} triage decision` : 'Triage decision',
    }));
}

/**
 * One handover row per uploaded document (TDI-612), from the rows
 * rmsListReferralDocuments returns. Keyed upload:{id} so the "Added to EMIS"
 * tick is stored like every other row's. A Word upload whose PDF copy is not
 * ready yet is listed but not available, so it cannot be downloaded or
 * filed as anything but its PDF.
 *
 * @param {object[]} uploads
 * @returns {{ key: string, title: string, detail: string, available: boolean, request: object, upload: object }[]}
 */
export function uploadDownloadRows(uploads) {
  return (Array.isArray(uploads) ? uploads : []).filter((u) => u && u.id).map((upload) => ({
    key: `upload:${upload.id}`,
    title: upload.description || upload.documentTypeLabel || 'Uploaded document',
    detail: upload.pdfReady === false
      ? 'Preparing PDF'
      : [upload.documentTypeLabel, `Provided by ${String(upload.providedByLabel || '').toLowerCase()}`]
        .filter(Boolean).join(' · '),
    available: upload.pdfReady !== false,
    request: { document: 'upload', documentId: upload.id },
    upload,
  }));
}
